// ============================================================
// BackToTop — a per-panel scroll-to-top arrow.
// ------------------------------------------------------------
// Replaces the app-level arrow that used to live in Workspace (phone only,
// fixed to the viewport, scrolling the whole workspace column). That one had to
// guess what you meant by "top"; a panel doesn't — its top is its own top.
//
// HOW IT FINDS THE SCROLLER — and why it doesn't take a scrollRef:
//
// You'd think the caller could just hand us the element that scrolls. It can't,
// reliably. In the Journal, the scroller MOVES: WalletHistoryPanel puts overflow
// on its outer div when isLandscape (phone), and on the inner history column
// when not (desktop). A ref pinned to either one is wrong half the time, and
// it'd go stale the moment the breakpoint flips.
//
// So we listen on the panel ROOT in the CAPTURE phase. Scroll events don't
// bubble — but they do capture — so a listener up here sees every descendant
// scroller, and `e.target` IS the element doing the scrolling. Exact, not a
// heuristic. It re-targets itself for free when the layout changes, and it means
// panels like the Journal need ZERO changes to the components they wrap.
// (Workspace's old arrow used this same capture trick; it's the one part of it
// worth keeping.)
//
// REQUIREMENT ON THE CALLER: containerRef must point at an element that is
// position:relative and is NOT itself the scroller. If the root scrolled, an
// absolutely-positioned button inside it would scroll away with the content —
// which is the entire bug we're fixing. Both current callers satisfy this:
// .scan-panel-wrap holds the scrolling .scan-panel, and .journal-panel is
// overflow:hidden over whatever WalletHistoryPanel is doing inside.
// ============================================================
import { useState, useEffect, useRef } from 'react'

export default function BackToTop({ containerRef, threshold = 240, inset = 12 }) {
  const [show, setShow] = useState(false)
  // The element the user is actually scrolling, captured live. Not state — it
  // changes on every scroll event and nothing renders off it.
  const scrollerRef = useRef(null)

  useEffect(() => {
    const root = containerRef?.current
    if (!root) return

    const onScroll = (e) => {
      const el = e.target
      if (!el || typeof el.scrollTop !== 'number' || !el.scrollHeight) return
      // Ignore scrollers with nothing to scroll (a short list, a fixed calendar).
      if (el.scrollHeight <= el.clientHeight + 4) return
      scrollerRef.current = el
      setShow(el.scrollTop > threshold)
    }

    root.addEventListener('scroll', onScroll, true)   // capture: scroll doesn't bubble
    return () => root.removeEventListener('scroll', onScroll, true)
  }, [containerRef, threshold])

  if (!show) return null

  return (
    <>
      <style>{`
        @keyframes btt-fade {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 0.92; transform: translateY(0); }
        }
      `}</style>
      <button
        aria-label="Back to top"
        title="Back to top"
        onClick={() => {
          scrollerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
          setShow(false)
        }}
        style={{
          position: 'absolute',
          right: inset,
          bottom: inset,
          zIndex: 30,
          width: 32,
          height: 32,
          padding: 0,
          background: 'var(--surface-rail, #14141d)',
          border: '1px solid var(--accent, #f0a72b)',
          color: 'var(--accent, #f0a72b)',
          fontSize: 15,
          lineHeight: '30px',
          textAlign: 'center',
          opacity: 0.92,
          cursor: 'pointer',
          // The house double-chamfer, same cut as the old arrow — scaled 8→6px
          // for a 32px button instead of 40.
          clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
          animation: 'btt-fade 0.18s ease forwards',
        }}
      >↑</button>
    </>
  )
}
